import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LogOut, Rocket, LayoutDashboard, UserCircle2, ShieldAlert,
  MessageSquareHeart, Wine as BottleIcon, BookOpen as BookIcon,
  Gift, Clock3,
} from "lucide-react";
import NotificationsBell from "./NotificationsBell";
import { VerifyEmailBanner } from "./VerifyEmailBanner";

import { _t } from "@/lib/i18n";
/**
 * Primary navigation config.
 * WIP entries are shown but visually flagged and their click is intercepted
 * so no route resolution ever leaks the internal state.
 */
const NAV_ITEMS = [
  { to: "/dashboard", label: _t("Dashboard"), icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/wizard", label: _t("Nuova capsula"), icon: Rocket, testId: "nav-create" },
  { to: "/bacheca", label: _t("Bacheca"), icon: MessageSquareHeart, testId: "nav-bacheca" },
  { to: "/miab", label: "MIAB", icon: BottleIcon, testId: "nav-miab" },
  { to: "/racconti", label: _t("Racconti"), icon: BookIcon, testId: "nav-racconti" },
  { to: "/voucher", label: _t("Voucher"), icon: Gift, testId: "nav-voucher", wip: true },
  { to: "/profilo", label: _t("Profilo"), icon: UserCircle2, testId: "nav-profile" },
];

function NavItem({ item, compact = false }) {
  const Icon = item.icon;
  const base = `${compact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} rounded-full transition-colors whitespace-nowrap`;
  if (item.wip) {
    return (
      <button
        type="button"
        data-testid={item.testId}
        onClick={(e) => { e.preventDefault(); }}
        title={_t("Funzione in preparazione — sarà attiva a breve")}
        className={`${base} text-white/40 cursor-not-allowed hover:text-white/50 flex items-center gap-2`}
      >
        <Icon className="w-4 h-4" />
        <span>{item.label}</span>
        <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-[1px] rounded-full border border-white/15 text-white/50">{_t("WIP")}</span>
      </button>
    );
  }
  return (
    <NavLink
      to={item.to}
      data-testid={item.testId}
      className={({ isActive }) =>
        `${base} ${isActive ? "text-[#FF5722] bg-[#FF5722]/10" : "text-white/70 hover:text-white"}`
      }
    >
      <span className="inline-flex items-center gap-2">
        <Icon className="w-4 h-4" /> {item.label}
      </span>
    </NavLink>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/dashboard" data-testid="nav-logo" className="flex items-center gap-3 group flex-shrink-0">
            <img src="/images/img1.png" alt={_t("Time Capsule")} className="w-10 h-10 rounded-lg" />
            <div className="leading-none whitespace-nowrap">
              <div className="font-display text-2xl text-white">{_t("Time Capsule")}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{_t("Il futuro è già qui")}</div>
            </div>
          </Link>

          {/* Desktop / wide screens: full nav */}
          <nav className="hidden xl:flex items-center gap-1" data-testid="nav-desktop">
            {NAV_ITEMS.map((it) => (
              <NavItem key={it.to} item={it} />
            ))}
            {user?.is_admin && (
              <NavLink to="/admin/convenzioni" data-testid="nav-admin" className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm transition-colors ${isActive ? "text-[#FF5722] bg-[#FF5722]/10" : "text-white/70 hover:text-white"}`
              }>
                <span className="inline-flex items-center gap-2"><ShieldAlert className="w-4 h-4" />{" "}{_t("Admin")}</span>
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user && <NotificationsBell />}
            {user && (
              <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-white/10">
                <div className="text-right leading-tight">
                  <div className="text-sm text-white" data-testid="header-username">@{user.username}</div>
                  <div className="text-[10px] text-white/40">{user.email}</div>
                </div>
                <button data-testid="logout-btn" onClick={handleLogout} className="btn-ghost !p-2" title={_t("Esci")}>
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile / tablet / medium desktop: horizontally-scrollable secondary bar
            so ALL modules are reachable on every viewport up to xl (incl. MIAB,
            Racconti, Voucher WIP). */}
        {user && (
          <div className="xl:hidden border-t border-white/5 bg-black/30 backdrop-blur-md" data-testid="nav-mobile">
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-1 overflow-x-auto scrollbar-hide">
              {NAV_ITEMS.map((it) => (
                <NavItem key={it.to} item={it} compact />
              ))}
              {user?.is_admin && (
                <NavLink to="/admin/convenzioni" data-testid="nav-admin-mobile" className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${isActive ? "text-[#FF5722] bg-[#FF5722]/10" : "text-white/70 hover:text-white"}`
                }>
                  <span className="inline-flex items-center gap-2"><ShieldAlert className="w-4 h-4" />{" "}{_t("Admin")}</span>
                </NavLink>
              )}
            </div>
          </div>
        )}
      </header>

      <VerifyEmailBanner />
      <main className="max-w-7xl mx-auto px-6 py-10">{children}</main>

      <footer className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-xs text-white/40">
        <div>{_t("© {0} Time Capsule · Il futuro è già qui", new Date().getFullYear())}</div>
        <div className="flex gap-4">
          <Link to="/come-funziona" className="hover:text-white">{_t("Come funziona")}</Link>
          <Link to="/sicurezza" className="hover:text-white">{_t("Sicurezza")}</Link>
          <Link to="/prezzi" className="hover:text-white">{_t("Prezzi")}</Link>
          <Link to="/faq" className="hover:text-white">{_t("FAQ")}</Link>
        </div>
      </footer>
    </div>
  );
}
